import type { GuidedSetupStep } from '../components/guided-setup/guided-setup-steps';

/**
 * Guided-tour steps for the **Hook policy** tab (`/settings/scan-policy/hook-policy`, plan 2026-10-21 Task 5).
 *
 * Lives beside `auto-scan-tour-steps.ts` for the same reason: the registry imports every step set, and keeping them in
 * `pages/` keeps that import list flat.
 *
 * **Every `anchor` is the value of a `data-tour-step` ATTRIBUTE on a real element, never a CSS selector.** The scope
 * strip and the PR SAST note are the page's own; the source, Moments and Save anchors are set by the editor only in
 * `hookPolicyOnly` mode. A step whose anchor is absent (a read error, a workspace not chosen yet) is SKIPPED.
 *
 * Copy discipline: no step claims a state the page has not read. It does not say SAST is on or off for anyone.
 *
 * Host-owned (`GuidedTourHostComponent`): the permanent help trigger is the on-demand re-open path
 * (`.claude/rules/new-section-onboarding-coverage.md` R2), so the page renders no trigger of its own.
 */

/** Tour id — must appear in `UserTourController.ALLOWED_TOUR_IDS` (<= 32 chars). */
export const HOOK_POLICY_TOUR_ID = 'hook-policy';

export const HOOK_POLICY_TOUR_STEPS: readonly GuidedSetupStep[] = [
  {
    id: 'hp-scope',
    title: 'Organization or workspace',
    anchor: 'hook-policy-scope',
    body: [
      'Hook policy decides what Purple’s local hooks do on git commit, after git push and on gh pr create. ' +
        'The organization policy is the minimum for every workspace; a workspace can only make it stricter.',
      'There is no repository-level hook policy: a repository follows its workspace.',
    ],
  },
  {
    id: 'hp-source',
    title: 'Where these values come from',
    anchor: 'hook-policy-source',
    body: [
      'This line says whether you are looking at the system default, a saved organization policy, a ' +
        'workspace’s own policy, or values inherited or raised to the organization minimum, and what Save will do.',
      'Opening the page or switching scope never saves anything.',
    ],
  },
  {
    id: 'hp-moments',
    title: 'One row per moment',
    anchor: 'hook-policy-moments',
    body: [
      'Each moment has an action (Off, Warn, Receipt or Block where offered), the domains it asks for, the scan ' +
        'policy, remedies and the verdict lists. When After push and Pull request scan different domains, a notice ' +
        'offers to copy After push’s domains into the draft.',
    ],
  },
  {
    id: 'hp-pr-sast',
    title: 'Hook SAST also needs PR SAST',
    anchor: 'hook-policy-pr-sast',
    body: [
      'A moment’s SAST runs only where PR Policy → Scans also allows SAST for the repository. Clearing SAST in a ' +
        'moment turns it off even where PR SAST is on.',
      'Where PR SAST is off, a moment that asks for SAST reports it as missing coverage: Warn warns, Block blocks, ' +
        'and no pass or receipt is issued for it.',
    ],
  },
  {
    id: 'hp-save',
    title: 'Save, or discard the draft',
    anchor: 'hook-policy-save',
    body: [
      'Save needs permission to manage enforcement policy; everyone else sees this page read-only. If someone else ' +
        'saved first, Reload shows their version; your draft is kept until you reload or discard it.',
    ],
  },
];
